import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, Copy, Check, ArrowRight, Instagram } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RunClubBadgePage() {
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setClub(null);
    setNotFound(false);
    fetch(`${API}/api/runclubs/badge-data/${clubId}`)
      .then((r) => { if (!r.ok) throw new Error('404'); return r.json(); })
      .then(setClub)
      .catch(() => setNotFound(true));
  }, [clubId]);

  useEffect(() => {
    if (club) document.title = `${club.name} — NinjAthlete Run Club Score ${club.score}/100`;
    return () => { document.title = 'NinjAthlete'; };
  }, [club]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="badge-not-found">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Club not found</h1>
          <Link to="/blogs/article/the-best-running-clubs-across-the-usa-where-community-meets-the-pavement" className="text-[#C7FA3E] text-sm hover:underline">
            See the full ranking →
          </Link>
        </div>
      </div>
    );
  }

  if (!club) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#C7FA3E] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const badgeUrl = `${API}/api/runclubs/badge/${club.id}.png`;
  const guidePath = `/blogs/article/${club.guide_slug}`;
  const caption = `We just scored ${club.score}/100 in NinjAthlete's Best Running Clubs in America (2026) — one of only 34 clubs in the country to make the cut. 🏃\n\nFull ranking: ninjathlete.com${guidePath}\n\n@ninjathlete #runclub #running #${club.city.replace(/\s+/g, '').toLowerCase()}`;

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = caption;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#050505] py-14 px-6" data-testid="runclub-badge-page">
      <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
        {/* Badge preview */}
        <div>
          <img
            src={badgeUrl}
            alt={`${club.name} — NinjAthlete Run Club Score ${club.score}/100`}
            className="w-full border border-zinc-800"
            data-testid="badge-image"
          />
        </div>

        {/* Kit */}
        <div>
          <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Official score card · 2026
          </p>
          <h1 className="text-white text-4xl sm:text-5xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }} data-testid="badge-club-name">
            {club.name} made the list.
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed mb-6">
            {club.name} scored <strong className="text-white">{club.score}/100 — {club.grade}</strong> in NinjAthlete's
            {' '}<em>Best Running Clubs in America (2026)</em>, one of only 34 clubs nationwide to make the cut.
            This page is your shareable kit: download the badge, post it, tag <a href="https://www.instagram.com/ninjathlete/" target="_blank" rel="noopener noreferrer" className="text-[#C7FA3E] hover:underline">@ninjathlete</a>.
          </p>

          {/* Score breakdown */}
          <div className="mb-7 space-y-2.5" data-testid="badge-breakdown">
            {club.breakdown.map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="text-zinc-500 text-xs w-44 flex-shrink-0">{b.label}</span>
                <div className="flex-1 h-1.5 bg-zinc-800 overflow-hidden rounded-full">
                  <div className="h-full bg-[#C7FA3E]" style={{ width: `${(b.value / b.max) * 100}%` }} />
                </div>
                <span className="text-zinc-300 text-xs w-12 text-right">{b.value}/{b.max}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <a
              href={badgeUrl}
              download={`ninjathlete-runclub-score-${club.id}.png`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C7FA3E] hover:bg-[#b8e01f] text-black text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="badge-download-btn"
            >
              <Download className="w-4 h-4" /> Download badge
            </a>
            <button
              onClick={copyCaption}
              className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 hover:border-[#C7FA3E]/60 text-zinc-200 text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="badge-copy-caption-btn"
            >
              {copied ? <Check className="w-4 h-4 text-[#C7FA3E]" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy IG caption'}
            </button>
          </div>

          <div className="p-4 bg-zinc-900/60 border border-zinc-800 text-xs text-zinc-500 leading-relaxed mb-6" data-testid="badge-caption-preview">
            <p className="text-zinc-400 uppercase tracking-wider mb-2">Suggested caption</p>
            <p className="whitespace-pre-line text-zinc-300">{caption}</p>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <Link to={`${guidePath}#${club.id}`} className="inline-flex items-center gap-1.5 text-[#C7FA3E] hover:gap-2.5 transition-all" data-testid="badge-view-card-link">
              View your full scorecard in the guide <ArrowRight className="w-4 h-4" />
            </Link>
            {club.ig && (
              <a href={`https://www.instagram.com/${club.ig}/`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 transition-colors">
                <Instagram className="w-4 h-4" /> @{club.ig}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
